import React from 'react';
import PropTypes from 'prop-types';
import { Field, FieldArray, reduxForm, formValueSelector, change } from 'redux-form';
import { get } from 'lodash';
import { connect } from 'react-redux';
import ToggleField from '../app-main/components/reduxForms/fields/ToggleField/ToggleField';
import TextAreaField from 'shared-components/reduxForms/fields/TextAreaField/TextAreaField';
import validateAccessForm from './forms/validateAccessForm';
import onSubmitProductionForms from './forms/onSubmitProductionForms';
import AccessTypesFields from './AccessTypesFields';
import accessTypes from './data/accessTypes';
import { mapBoolToString, showFormError } from './data/utils';
import ButtonNavigation from '../app-faq-components/ButtonNavigation';
import { FormattedMessage, defineMessages, intlShape, injectIntl } from 'react-intl';

const AccessForm = ({
  error,
  handleSubmit,
  separateCrewArea,
  offLimits,
  intl,
  ...props
}) => {
  const translationMessages = defineMessages({
    errorMessage: {
      id: 'hosting.access_form.error_message',
      defaultMessage: 'Please select an option'
    },
    yes: {
      id: 'hosting.access_form.yes_label',
      defaultMessage: 'Yes'
    },
    no: {
      id: 'hosting.access_form.no_label',
      defaultMessage: 'No'
    }
  });

  return (
    <form className="flex-basis-100" onSubmit={handleSubmit}>
      <h2><FormattedMessage id="hosting.access_form.space_access_heading" defaultMessage="Space Access"/></h2>
      <p className="text-gray-600"><FormattedMessage id="hosting.access_form.space_access_text" defaultMessage="Video and photo shoots often involve unloading heavy equipment and will want to know the layout of your space."/></p>
      <div className="questions-section">
        <h6><FormattedMessage id="hosting.access_form.how_space_is_accessed_heading" defaultMessage="How is your space accessed?"/></h6>
        <span className="text-gray-600"><FormattedMessage id="hosting.access_form.how_space_is_accessed_options" defaultMessage="Select all that apply"/></span>
        <FieldArray
          component={AccessTypesFields}
          fields={accessTypes}
          errorMessage={intl.formatMessage(translationMessages.errorMessage)}
        />
      </div>

      <div className="questions-section">
        <h6 className="heavy"><FormattedMessage id="hosting.access_form.how_equipment_can_be_loaded_heading" defaultMessage="Describe how equipment can be loaded into the space"/></h6>
        <ul className="list-style-inside no-gutter text-gray-600 margin-lg-bottom">
          <li><FormattedMessage id="hosting.access_form.loading_zone_availability" defaultMessage="Is there a loading zone?"/></li>
          <li><FormattedMessage id="hosting.access_form.entrance_ramp_requirement" defaultMessage="Does the entrance require a ramp?"/></li>
          <li><FormattedMessage id="hosting.access_form.load_trucks_removal" defaultMessage="Do loading trucks need to be moved?"/></li>
        </ul>
        <Field
          name='equipment_loading_description'
          component={TextAreaField}
          maxLength={1000}
          attributes={{
            'data-testing-id': 'describe_loading'
          }}
        />
      </div>

      <div className="questions-section">
        <h6><FormattedMessage id="hosting.access_form.separate_room_for_crew_heading" defaultMessage="Is there a separate room for crew & talent?"/></h6>
        <p className="text-gray-600"><FormattedMessage id="hosting.access_form.separate_room_for_crew_text" defaultMessage="Shoots often require a staging area to set up makeup, wardrobe, etc."/></p>
        <Field
          name='has_separate_crew_area'
          component={ToggleField}
          onChange={() => props.dispatch(
            change('PRODUCTION_ACCESS_FORM', 'separate_crew_area_description', ''))
          }
          attributes={{
            'data-testing-id': 'has_separate_crew_area'
          }}
          firstOption={
            {
              id: 'has_separate_crew_area_true',
              value: 'true',
              label: intl.formatMessage(translationMessages.yes)
            }}
          secondOption={
            {
              id: 'has_separate_crew_area_false',
              value: 'false',
              label: intl.formatMessage(translationMessages.no)
            }}
        />
        {
          separateCrewArea &&
          <div className="margin-xl-top">
            <p className="heavy"><FormattedMessage id="hosting.access_form.separate_room_for_crew_options_label" defaultMessage="Which room?"/></p>
            <Field
              name='separate_crew_area_description'
              component={TextAreaField}
              height={0}
              attributes={{
                rows: 1,
                'data-testing-id': 'separate_crew_area_description'
              }}
              maxLength={1000}
            />
          </div>
        }
      </div>

      <div className="questions-section">
        <h6><FormattedMessage id="hosting.access_form.off_limits_rooms_heading" defaultMessage="Are any rooms or areas off-limits for shooting?"/></h6>
        <Field
          name='has_off_limit_areas'
          component={ToggleField}
          attributes={{
            'data-testing-id': 'has_off_limit_areas'
          }}
          onChange={() => props.dispatch(
            change('PRODUCTION_ACCESS_FORM', 'off_limit_areas_description', ''))
          }
          firstOption={
            {
              id: 'has_off_limit_areas_true',
              value: 'true',
              label: intl.formatMessage(translationMessages.yes)
            }}
          secondOption={
            {
              id: 'has_off_limit_areas_false',
              value: 'false',
              label: intl.formatMessage(translationMessages.no)
            }}
        />
        {
          offLimits &&
          <div className="margin-xl-top">
            <p className="heavy"><FormattedMessage id="hosting.access_form.off_limits_rooms_options_label" defaultMessage="Which rooms or areas are off-limit to shoot?"/></p>
            <Field
              name='off_limit_areas_description'
              component={TextAreaField}
              attributes={{
                'data-testing-id': 'separate_crew_area_description'
              }}
              maxLength={1000}
            />
          </div>
        }
      </div>

      <div className="questions-section">
        <h6><FormattedMessage id="hosting.access_form.exterior_filming_heading" defaultMessage="Can the exterior of the building be filmed or photographed?"/></h6>
        <Field
          name='can_film_exterior'
          component={ToggleField}
          attributes={{
            'data-testing-id': 'can_film_exterior'
          }}
          firstOption={
            {
              id: 'can_film_exterior_true',
              value: 'true',
              label: intl.formatMessage(translationMessages.yes)
            }}
          secondOption={
            {
              id: 'can_film_exterior_false',
              value: 'false',
              label: intl.formatMessage(translationMessages.no)
            }}
        />
      </div>
      <hr className="space-type-line-break"/>
      <div className="text-danger text-small margin-xl-bottom">{error}</div>
      <ButtonNavigation {...props} />
    </form>
  );
};

AccessForm.propTypes = {
  handleSubmit: PropTypes.func,
  dispatch: PropTypes.func,
  error: PropTypes.string,
  separateCrewArea: PropTypes.bool,
  offLimits: PropTypes.bool,
  spaceId: PropTypes.string,
  intl: intlShape
};

const config = {
  form: 'PRODUCTION_ACCESS_FORM',
  onSubmit: onSubmitProductionForms,
  validate: validateAccessForm,
  onSubmitFail: showFormError
};

const selector = formValueSelector('PRODUCTION_ACCESS_FORM');

function mapStateToProps (state) {
  const separateCrewArea = selector(state, 'has_separate_crew_area');
  const offLimits = selector(state, 'has_off_limit_areas');
  const productionDetails = get(state, 'space.data.production_details', {});
  const spaceAccessTypes = get(productionDetails, 'space_access_types', []);
  const spaceAccessTypesToObject = spaceAccessTypes.reduce((acc, currentVal) => {
    acc[currentVal] = currentVal;
    return acc;
  }, {});

  return {
    separateCrewArea: 'true' === separateCrewArea,
    offLimits: 'true' === offLimits,
    initialValues: {
      ...mapBoolToString(productionDetails),
      space_access_types: spaceAccessTypesToObject
    }
  };
}

export default connect(mapStateToProps)(reduxForm(config)(injectIntl(AccessForm)));
